// Shared gig domain types — single source of truth for the app (src/) and Cloud Functions (functions/).
// This module MUST stay self-contained (no imports) so it compiles cleanly under both the app's
// `bundler` resolution and functions' `nodenext` resolution. See /shared/tsconfig.json.
//
// Originally generated by Firefoo v1.5.11 on 2023-11-11; relocated here from src/types/gig.ts.
// gigStatuses / gigStatusesWithEmail were moved here from src/constants/optionLists.ts (re-exported there).
//
// ⚠️ DUAL AUDIENCE: the comments in this file (down to the runtime-init-constants marker near the
// bottom) are served VERBATIM to the AI assistant as `gigs` schema documentation, via
// functions/scripts/embed-schemas.cjs → the getCollectionSchema tool. Write field comments for BOTH
// developers and the assistant: prefer plain-language business meaning over restating the type.
// Everything below the marker (init constants) is excluded from that copy.
//
// NOTE: DashboardEventData / DashboardGig / dashboardEventDataInit stay in src/types/gig.ts — they
// are client-side composites the talent dashboard builds in the browser from the parent event doc
// (they reference Venue/FlexDetailsField/EntType from the event schema) and are NEVER stored on
// gig docs.

// --- Gig contract statuses (moved from constants/optionLists.ts) ---
/**
 * The talent-booking lifecycle, in rough pipeline order:
 *  'Pre-inquiry'      — role row created (usually generated from a service's role list); no outreach yet.
 *  'Inquired'         — availability inquiry sent to the talent. Also set automatically: when a sibling
 *                       gig (same event + role) goes Unavailable, the onGigUpdate trigger advances the
 *                       next Pre-inquiry gig in callOrder to Inquired and emails that talent.
 *  'Re-inquired'      — inquiry re-sent / follow-up.
 *  'Available'        — the talent pressed "Available" on their dashboard (agrees to hold the date).
 *  'Unavailable'      — the talent declined; triggers the callOrder auto-advance above.
 *  'Selected'         — staff chose this talent for the role.
 *  'Pushed'           — full event details pushed to the talent for final review; their dashboard asks
 *                       them to read everything and press CONFIRM.
 *  'Confirmed'        — the talent pressed the confirm button after reviewing the pushed details.
 *  'Re-confirm'       — staff requests a fresh confirmation after details changed.
 *  'Not Selected'     — was inquired/available but not chosen.
 *  'Payment Approved' — approved for payout; the Gigs Report defaults to this filter for payment runs.
 *  'Paid'             — payout sent (the Gigs Report "mark paid" action sets this plus `paidDate`).
 * Stored values NOT selectable from this list: 'Cancelled' (currently written when an event is
 * marked dead), plus historical 'Client', 'Deleted', and the typo 'Pre-inqury'.
 */
export const gigStatuses = [
  'Pre-inquiry',
  'Inquired',
  'Re-inquired',
  'Available',
  'Selected',
  'Pushed',
  'Confirmed',
  'Re-confirm',
  'Unavailable',
  'Not Selected',
  'Payment Approved',
  'Paid'
] as const;
export type GigStatus = (typeof gigStatuses)[number];

/**
 * Values that may exist on stored gig documents but are not selectable from the current lifecycle
 * controls. `Cancelled` is still written when an event is marked dead; the others are historical.
 */
export type NonSelectableGigStatus = 'Cancelled' | 'Client' | 'Deleted' | 'Pre-inqury';

/** Any contract status that may be encountered on a persisted gig document. */
export type GigContractStatus = GigStatus | NonSelectableGigStatus;

/** Statuses that have a same-named status-email template. When a gig is manually moved into one of
 *  these, the UI offers to send the talent the matching email/text (template name == status name). */
export const gigStatusesWithEmail = [
  'Inquired',
  'Re-inquired',
  'Available',
  'Selected',
  'Pushed',
  'Re-confirm',
  'Payment Approved'
] as const;

/** One talent-facing note line (label + text). Shown to the talent on their dashboard under
 *  "Notes (for you only)"; the talent grid shows them in the "External Notes" column. */
export interface ContractorNote {
  desc: string;
  note: string;
}

// One doc per talent/role booking on an event — the rows of the event's Talent tab. A gig starts as
// an unfilled role (relVendorId empty) and is linked to a contact as talent is booked. Linked to its
// event via relEventId; queried and sorted by the denormalized relEventDate.
export interface Gig {
  /** This talent's arrival/call time (wall-clock, timezone-naive). Shown on the talent grid and the
   *  talent dashboard. Wall-clock and timezone-naive; the assistant writes it as `HH:mm` or
   *  `YYYY-MM-DDTHH:mm`, never with a timezone. */
  callTime?: Date | null;
  /** Legacy — no live reads or writes found. */
  category?: string;
  /** Legacy contract-link label — no live reads found. */
  contractLinkDesc_1?: string;
  /** Legacy contract-link URL — no live reads found. */
  contractLinkUrl_1?: string;
  /** Persisted booking lifecycle stage. Includes current selectable GigStatus values plus known
   *  system/historical NonSelectableGigStatus values. Changing it into one of gigStatusesWithEmail
   *  can email/text the talent; the assistant must be told explicitly whether to send. */
  contractStatus: GigContractStatus;
  /** Individual attire override for this talent; shown as "Attire (Individual)" on their dashboard. */
  contractAttire?: string;
  /** @deprecated Legacy {id,name} contact ref — superseded by relVendorId / relVendorName. */
  contractor?: Contractor;
  /** Talent-facing notes — rendered on the talent's dashboard under "Notes (for you only)". */
  contractorNotes?: ContractorNote[];
  createdBy: string;
  createdDate: Date | null;
  /** Firestore document id (also stored on the doc). */
  id: string;
  /** Staff-only note ("Internal Note" grid column); never shown to the talent. */
  internalNotes?: string;
  /** Legacy — nothing currently sets or reads it. */
  linkJobSheet?: Date | null;
  /** Audit stamp. onGigUpdate writes modifiedBy 'System' on automatic status advances. NOTE: grid
   *  row saves stamp `modified` (Date) + modifiedBy instead — see the `modified` field below. */
  modifiedBy: string;
  modifiedDate: Date | null;
  /** Legacy string time from the original data import; not maintained by current code. */
  modifiedTime: string;
  /** Stamped by grid row saves (useSaveRow) — the live "last modified" timestamp on most docs. */
  modified?: Date | null;
  /** Section-lead flag: shows this person in the dashboard Section Leads list, and the jobsheet
   *  talent roster suffixes their role with "(«section» Section Lead)". */
  isSectionLead?: boolean;
  /** Denormalized segment assignments. `id` is source of truth; `name` is a copy kept fresh by the
   *  onSegmentUpdate/onSegmentDelete triggers. */
  segments?: { id: string; name: string }[];
  /** Informational label shown to the talent ("This gig is at your prime rate."). No current writer —
   *  prime/non-prime pricing itself is applied to rates[].amount via the event's eventPrime toggle. */
  prime?: 'Prime' | 'Non-prime';
  /** Talent-facing performance note shown on their dashboard ("Performance Note"). */
  performanceNotes?: string;
  /** Hide this row from the talent dashboard roster and the jobsheet vendor list. */
  hidden?: boolean;
  /** Algolia search-hit id that leaks onto docs created from search rows — noise; use `id`. */
  objectID?: string;
  /** Manual display order in the talent grid / jobsheet vendor list (new gigs append to the end). */
  order?: number;
  /** Pay line items for this booking; their sum is the talent's total pay. Visibility is
   *  permission-gated (View Contractor Rates / Top Secret). See Rate. `type === 'baseRate'` is the
   *  singular base-fee sentinel; extra lines link to a templateTalentCosts entry by name only
   *  (search with searchTalentCosts first). */
  rates: Rate[];
  /** Projected extra costs copied from the service role's talent-cost templates (e.g. Travel,
   *  Rehearsal). Each awaits an accept/reject decision in the rates dialog — pending ones flag a
   *  warning in the grid; accepting one inserts a real `rates` line. See TalentCostTemplate. */
  extraProjections?: TalentCostTemplate[];
  /** Server timestamp stamped when the Gigs Report marks the gig Paid. */
  paidDate?: Date | null;
  /** Denormalized copy of the parent event's dateStart (normalized) — the field gigs are queried and
   *  sorted by. Re-stamped in bulk when the event date changes or gigs are reassigned. Set once at
   *  creation; the assistant cannot move a gig between events. */
  relEventDate: Date | null;
  /** Parent event doc id. Set once at creation; the assistant cannot move a gig between events. */
  relEventId: string;
  /** Denormalized parent event title. Set once at creation; the assistant cannot move a gig between
   *  events. */
  relEventName: string;
  /** The `services` line item this role bills under (links gig costs to the service). */
  relServiceId?: string;
  /** The service template that generated this role; used to re-link the gig to the concrete service
   *  instance when that service is added to the event. */
  relServiceTemplateId?: string;
  /** Contact id of the booked talent (the "vendor"/contractor). Empty while the role is unfilled. */
  relVendorId: string;
  /** Denormalized talent display name (grid "Name" column). */
  relVendorName?: string;
  /** Legacy single-rate copy — superseded by `rates`. */
  relVendorRate?: number;
  /** Write-in role label — displayed as the role whenever `role` (a tag id) is empty. */
  relVendorSkill?: string;
  /** Talent-tag ID from the "All Tags › Talent" tag tree identifying the role/instrument. An id, not
   *  a name — assistant tools return it already translated; the UI resolves it via the tag maps. */
  role?: string;
  /** 1-based position in this role's call list ("1st call", "2nd call"…). Drives the
   *  Unavailable → auto-inquire-next-call chain (onGigUpdate). */
  callOrder?: number;
  /** Legacy copy of the contact's day wage — no live reads found; not maintained. */
  talWageD?: number;
  /** Derived: the role tag's top-level ancestor in the talent tag tree (e.g. "Musical Talent").
   *  Recomputed client-side from `role` for grid filters and the jobsheet musician split — stored
   *  values are stale snapshots; derive from `role` instead of trusting them. Not written by the app
   *  or the assistant; always derive from `role`. */
  topParent: string | null;
  /** Restricted booking: rates/row hidden from everyone without "Top Secret" access, and the gig is
   *  excluded from the jobsheet vendor list entirely. */
  topSecret: boolean;
  /** Derived: one-up ancestor (immediate section) of the role tag — see topParent. Not written by the
   *  app or the assistant; always derive from `role`. */
  oneUpParent: string | null;
  /** Derived: two-up ancestor of the role tag — see topParent. Not written by the app or the
   *  assistant; always derive from `role`. */
  twoUpParent: string | null;
}

/** @deprecated Legacy contact ref — see Gig.contractor. */
export interface Contractor {
  id: string;
  name: string;
}

/** One pay line. `type` is 'baseRate' for the core performance fee (amount looked up from the
 *  contact's prime/non-prime rate for the act's rate category), 'Rehearsal' on rehearsal-rate
 *  events, or — for extra lines — a talent-cost template name or free text (e.g. 'Travel',
 *  'Overtime'). `amount` is dollars. */
export interface Rate {
  amount: number;
  type: string;
}

/** Review outcome for a projected extra cost: accepted = converted into a real `rates` line;
 *  rejected = excluded from cost KPIs. Unset = still pending review. */
export type ProjectionDecision = 'accepted' | 'rejected';

/** Snapshot of a `templateTalentCosts` doc attached to a service role / gig as a projected extra
 *  cost (see Gig.extraProjections). `amount` may be overridden per contact via the contact's
 *  assignedTalentCosts subcollection. */
export interface TalentCostTemplate {
  id: string;
  name: string;
  description?: string;
  amount?: number;
  projectionDecision?: ProjectionDecision;
  createdAt?: Date;
  modifiedAt?: Date;
}

/** Per-contact override for a talent-cost template — the shape of docs in
 *  contacts/{contactId}/assignedTalentCosts/{templateId} (NOT stored on gig docs). */
export interface TalentCostAssignment {
  amount: number;
  templateName: string;
  modifiedAt: any;
}

// --- runtime init constants (below this line: excluded from assistant schema docs) ---

export const gigInit = {
  callTime: '',
  category: '',
  contractLinkDesc_1: '',
  contractLinkUrl_1: '',
  contractStatus: '',
  contractor: {
    id: '',
    name: ''
  },
  contractorNotes: [],
  createdBy: '',
  createdDate: null,
  internalNotes: '',
  linkJobSheet: null,
  modifiedBy: '',
  modifiedDate: null,
  modifiedTime: '',
  isSectionLead: false,
  prime: '',
  order: 0,
  rates: [
    {
      amount: 0,
      type: 'baseRate'
    }
  ],
  extraProjections: [],
  relEventDate: null,
  relEventId: '',
  relEventName: '',
  relServiceId: '',
  relServiceTemplateId: '',
  relVendorId: '',
  relVendorName: '',
  relVendorRate: 0,
  relVendorSkill: '',
  role: '',
  talWageD: 0,
  topParent: ''
};
